import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { track } from "@/lib/meta";
import { trackConversionPixel } from "@/lib/oaiq";
import { Loader2, Sparkles } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { gotoApp } from "@/lib/appUrl";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function GoogleCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const redirectUri = window.location.origin + "/auth/google";

    (async () => {
      if (!code || !state) return navigate("/login", { replace: true });
      try {
        const { data } = await api.post("/auth/google/callback", { code, state, redirect_uri: redirectUri });
        localStorage.setItem("nv_token", data.access_token);
        setUser(data.user);
        if (data.new_account) {
          // same event_id as the server copy (reg_<user_id>) so Meta counts one registration
          track("CompleteRegistration", { status: "google", currency: "INR", value: 0 },
                { eventId: `reg_${data.user.id}`, email: data.user.email, browserOnly: true });
          trackConversionPixel("trial_started", { type: "plan_enrollment" },
                               data.oai_event_id);
        } else {
          track("Login", { method: "google" }, { browserOnly: true });
        }
        const sub = await api.get("/billing/subscription").catch(() => null);
        const needsPlan = sub?.data && (sub.data.status === "expired" || sub.data.proposals_left <= 0);
        const target = needsPlan ? "/plans" : "/dashboard";
        if (await gotoApp(data.access_token, target)) return;
        navigate(target, { replace: true });
      } catch (e) {
        setError(apiError(e));
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen nv-grid-bg grid place-items-center px-4">
      <div className="w-full max-w-[380px] text-center nv-rise">
        <div className="mx-auto mb-5 flex justify-center"><Logo size={34} /></div>
        {error ? (
          <>
            <p data-testid="google-callback-error" className="text-sm text-red-400">{error}</p>
            <button
              data-testid="google-callback-retry"
              onClick={() => navigate("/login", { replace: true })}
              className="mt-5 h-10 px-4 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <p data-testid="google-callback-status" className="text-sm text-zinc-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Signing you in…
          </p>
        )}
      </div>
    </div>
  );
}
